import React, { useState, useEffect } from "react";
import { Plus, X, Save, ArrowLeft } from "lucide-react";
import "./AddMultipleSynonyms.scss";

const AddMultipleSynonyms = ({
	isEditMode = false,
	initialEntry = null,
	onSave,
	onClose,
	websiteId,
}) => {
	const [entries, setEntries] = useState(
		isEditMode && initialEntry
			? [
				{
					...initialEntry,
					synonyms: Array.isArray(initialEntry.synonyms)
						? initialEntry.synonyms.join(", ")
						: initialEntry.synonyms || "",
				},
			]
			: [{ searchTerm: "", synonyms: "" }]
	);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);

	useEffect(() => {
		if (isEditMode && initialEntry) {
			const currentEntry = entries[0];
			const initialSynonyms = Array.isArray(initialEntry.synonyms)
				? initialEntry.synonyms.join(", ")
				: initialEntry.synonyms || "";

			const searchTermChanged =
				currentEntry.searchTerm !== initialEntry.searchTerm;
			const synonymsChanged = currentEntry.synonyms !== initialSynonyms;

			setHasChanges(searchTermChanged || synonymsChanged);
		} else {
			const hasValidEntries = entries.some(
				(entry) => entry.searchTerm.trim() && entry.synonyms.trim()
			);
			setHasChanges(hasValidEntries);
		}
	}, [entries, initialEntry, isEditMode]);

	const handleAddRow = () => {
		setEntries([...entries, { searchTerm: "", synonyms: "" }]);
	};

	const handleRemoveRow = (index) => {
		if (entries.length === 1) return;
		const newEntries = [...entries];
		newEntries.splice(index, 1);
		setEntries(newEntries);
	};

	const handleInputChange = (index, field, value) => {
		const updatedEntries = [...entries];
		updatedEntries[index] = { ...updatedEntries[index], [field]: value };
		setEntries(updatedEntries);

		if (isEditMode && initialEntry) {
			const currentEntry = updatedEntries[index];
			const initialSynonyms = Array.isArray(initialEntry.synonyms)
				? initialEntry.synonyms.join(", ")
				: initialEntry.synonyms || "";

			const searchTermChanged =
				field === "searchTerm"
					? value !== initialEntry.searchTerm
					: currentEntry.searchTerm !== initialEntry.searchTerm;

			const synonymsChanged =
				field === "synonyms"
					? value !== initialSynonyms
					: currentEntry.synonyms !== initialSynonyms;

			setHasChanges(searchTermChanged || synonymsChanged);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsSubmitting(true);

		try {
			if (isEditMode) {
				const entry = entries[0];
				if (!entry.searchTerm.trim()) {
					setIsSubmitting(false);
					return;
				}

				const formattedData = {
					...entry,
					searchTerm: entry.searchTerm.trim(),
					synonyms: entry.synonyms
						.toString()
						.split(",")
						.map((s) => s.trim())
						.filter(Boolean),
					...(isEditMode && initialEntry?.id && { id: initialEntry.id }),
				};

				await onSave(formattedData);
			} else {
				const validEntries = entries.filter(
					(entry) => entry.searchTerm.trim() && entry.synonyms
				);

				if (validEntries.length === 0) {
					setIsSubmitting(false);
					return;
				}

				const formattedData = validEntries.map((entry) => ({
					searchTerm: entry.searchTerm.trim(),
					synonyms: entry.synonyms
						.toString()
						.split(",")
						.map((s) => s.trim())
						.filter(Boolean),
					websiteId,
				}));

				await onSave(formattedData);
			}

			onClose();
		} catch (error) {
			console.error("Error saving synonyms:", error);
			throw error;
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="add-multiple-synonyms">
			<div className="page-header">
				<div className="header-content">
					<div className="page-header">
						<p className="subtitle" style={{width: "100%"}}>
							{isEditMode
								? "Edit the search term and its synonyms to ensure relevant matches. Adjust the main term users will search for, and update synonyms separated by commas. Confirm your updates to help user searches map to the right results."
								: "Add search terms and synonyms to enhance result matching. Separate synonyms with commas. You can add multiple rows. Make sure each entry is relevant and clearly related."
							}
						</p>
					</div>
				</div>
			</div>
			<form onSubmit={handleSubmit} className="synonyms-form">
				<div className="form-card">
					<div className="form-header">
						<div className="form-group">
							<label>Search Term</label>
						</div>
						<div className="form-group">
							<label>Synonyms</label>
						</div>
						<div className="form-actions">
							{!isEditMode && (
								<button
									type="button"
									onClick={handleAddRow}
									className="add-row-button"
								>
									<Plus size={14} /> Add Row
								</button>
							)}
						</div>
					</div>

					<div className="entries-container">
						{entries.map((entry, index) => (
							<div key={index} className="entry-row">
								<div className="form-group">
									<input
										type="text"
										className="form-control"
										value={entry.searchTerm}
										onChange={(e) =>
											handleInputChange(index, "searchTerm", e.target.value)
										}
										placeholder="e.g., laptop"
										required
									/>
								</div>
								<div className="form-group">
									<input
										type="text"
										className="form-control"
										value={entry.synonyms}
										onChange={(e) =>
											handleInputChange(index, "synonyms", e.target.value)
										}
										placeholder="e.g., notebook, macbook, ultrabook"
									/>
								</div>
								{/* <div className="form-actions">
                  <button
                    type="button"
                    className={`remove-button ${
                      entries.length === 1 ? "disabled" : ""
                    }`}
                    onClick={() => handleRemoveRow(index)}
                    disabled={entries.length === 1}
                    title={
                      entries.length === 1
                        ? "At least one row is required"
                        : "Remove row"
                    }
                  >
                    <X size={14} />
                  </button>
                </div> */}
							</div>
						))}
					</div>

					<div className="form-footer">
						<div className="action-buttons">
							<button
								type="button"
								className="btn btn-outline-secondary me-2"
								onClick={onClose}
								disabled={isSubmitting}
							>
								Cancel
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={
									isSubmitting ||
									(isEditMode
										? !hasChanges || !entries[0]?.searchTerm.trim()
										: entries.every((e) => !e.searchTerm.trim() || !e.synonyms))
								}
							>
								{isSubmitting ? (
									<>
										<span
											className="spinner-border spinner-border-sm me-1"
											role="status"
											aria-hidden="true"
										/>
										{isEditMode ? "Updating..." : "Saving..."}
									</>
								) : (
									<>
										<Save size={16} className="me-1" />
										{isEditMode ? "Update Synonym" : "Create Synonym"}
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			</form>
		</div>
	);
};

export default AddMultipleSynonyms;
